import {ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, Injectable, signal} from "@angular/core";
import {AntallMeldingerSendtRespons, DokumentId, DokumentRessursClient, DokumentSignatarerModell, DokumentsigneringRessursClient, DokumentVersjonModell, HtmlTekst} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {signatarerSomIkkeHarSignert} from "felles-ng2/modell/dokumentSignatarerModellHjelpere";
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {Observable} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatToolbar} from "@angular/material/toolbar";
import {TranslateModule} from "@ngx-translate/core";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {MedlemmerListevisningKompaktKomponent} from "web-ng2/moduler/medlem/komponenter/medlemmer-listevisning-kompakt/medlemmer-listevisning-kompakt";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MeldingSendtIkonKomponent} from "web-ng2/delt/komponenter/melding-sendt-ikon/melding-sendt-ikon";
import {tomDokumentSignatarerModell, tomDokumentVersjonModell} from "felles-ng2/modell/tommeModeller";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";

const debug = initDebugger('SigneringSendPåminnelseDialogKomponent');

/**
 * Dialog for utsendelse av signeringspåminnelse
 */
@Component({
    selector: 'signering-send-paaminnelse-dialog',
    templateUrl: 'signering-send-påminnelse-dialog.html',
    styleUrl: 'signering-send-påminnelse-dialog.scss',
    imports: [
        MatIcon,
        MatIconButton,
        MatToolbar,
        TranslateModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatFormField,
        MatLabel,
        ReactiveFormsModule,
        VisSpinnerDirektiv,
        TipsKomponent,
        FormaterZonedDateTimePipe,
        FormsModule,
        MedlemmerListevisningKompaktKomponent,
        MatProgressBar,
        MeldingSendtIkonKomponent,
        RiktekstRedigeringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SigneringSendPåminnelseDialogKomponent {

    static vis(dialog: MatDialog, dokumentId: DokumentId): MatDialogRef<SigneringSendPåminnelseDialogKomponent, SigneringSendPåminnelseDialogRespons> {
        return dialog.open(SigneringSendPåminnelseDialogKomponent, {
            data: {dokumentId} as DialogParametre,
            disableClose: true,
            ariaLabelledBy: 'signering-send-paaminnelse-dialog-tittel',
            minHeight: 400,
            maxWidth: 700});
    }

    modus = signal<'skjema' | 'kvittering'>('skjema');
    signatarer = signal<DokumentSignatarerModell>(tomDokumentSignatarerModell());
    dokumentversjon = signal<DokumentVersjonModell>(tomDokumentVersjonModell());
    harIkkeSignert = computed(() => signatarerSomIkkeHarSignert(this.signatarer()).map(signatar => signatar.medlem))
    purringSendesOmAntallDager = computed(() => this.beregnPurringSendesOmAntallDager())

    beskjedKontroller = new FormControl('', {nonNullable: true, validators: FeltValidatorer.htmlTekst});

    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());
    laster = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());
    antallSendt = signal(0);

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<SigneringSendPåminnelseDialogKomponent, SigneringSendPåminnelseDialogRespons>,
                private avhengigheter: SigneringSendPåminnelseDialogKomponentAvhengigheter,
                private oversettelse: Oversettelser,
                private destroyRef: DestroyRef) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());

        this.lastData(parametre.dokumentId);
    }

    async send() {
        await this.lagrer.utfør(async () => {
            const respons = await this.avhengigheter.sendPurring(this.dokumentversjon().dokumentId, this.beskjedKontroller.value);
            this.modus.set('kvittering');
            this.antallSendt.set(respons.antall);
        });
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }

    //private hjelpemetoder

    private lastData(dokumentId: DokumentId) {
        const lastSignatarer = this.laster.start()
        this.avhengigheter.signatarer(dokumentId)
          .pipe(lastSignatarer.avsluttVedLeveranse(), takeUntilDestroyed(this.destroyRef))
          .subscribe(signatarer => this.signatarer.set(signatarer));

        const lastDokument = this.laster.start()
        this.avhengigheter.dokumentTilSignering(dokumentId)
          .pipe(lastDokument.avsluttVedLeveranse(), takeUntilDestroyed(this.destroyRef))
          .subscribe(signatarer => this.dokumentversjon.set(signatarer));
    }


    private beregnPurringSendesOmAntallDager() {
        return this.signatarer().dagerTilNestePurring == 0 ?
          this.oversettelse.oversett(i18n('i dag')) :
          this.oversettelse.oversett(i18n('om {antall} dager'), {antall: this.signatarer().dagerTilNestePurring});
    }

}


export type SigneringSendPåminnelseDialogRespons = boolean;

export type DialogParametre = { dokumentId: DokumentId; };

@Injectable()
export class SigneringSendPåminnelseDialogKomponentAvhengigheter {

    constructor(private dokumentsigneringRessurs: DokumentsigneringRessursClient, private dokumentRessurs: DokumentRessursClient) {
    }

    signatarer(dokumentId: DokumentId): Observable<DokumentSignatarerModell> {
        return this.dokumentsigneringRessurs.signatarer(dokumentId).somObservable();
    }

    dokumentTilSignering(dokumentId: DokumentId): Observable<DokumentVersjonModell> {
        return this.dokumentRessurs.hentGjeldendeDokumentversjon(dokumentId).somObservable();
    }

    sendPurring(dokumentId: DokumentId, beskjed: HtmlTekst): Promise<AntallMeldingerSendtRespons> {
        return this.dokumentsigneringRessurs.sendPurring(dokumentId, {beskjed: beskjed}).somPromise();
    }
}
